import { IconographyTypes } from '@genial/icons';
import { useContext, useEffect, useMemo, useState } from 'react';

import { AbsoluteRouteDetector } from '../../contexts/AbsoluteRouteDetector';
import { angularFrameRoutesParams } from '../../routes/constants';
import { AllSignedInRoutes } from '../../routes/types';
import { sidebarIcons } from '../AppMenu/constants';

type RoutesWithKeyword = {
  [K in keyof AllSignedInRoutes]?: string[];
};

type FoundRoute = {
  routeName: string;
  routeParams?: Record<string, any>;
  routeText: string;
  isBeta?: boolean;
  menuIcon: IconographyTypes.Option;
};

export const useMenuSearchKeywords = (keyword?: string | null) => {
  const { labels } = useContext(AbsoluteRouteDetector.Context);
  const [foundRoutes, setFoundRoutes] = useState<FoundRoute[]>([]);

  const routesToSearch: RoutesWithKeyword = useMemo(() => {
    return {
      Home: ['home', 'inicio', 'beta'],
      AngularFrame_Home: ['home', 'inicio'],
      AngularFrame_Dashboard: ['dashboard', 'painel'],
      ManagementAnalysis: ['analise', 'gerencial', 'analise gerencial', 'beta'],
      AngularFrame_ManagementAnalysis: ['analise', 'gerencial', 'analise gerencial'],
      RateManagement: ['gerenciamento', 'taxas', 'gerenciamento de taxas', 'beta'],
      Cockpit: ['cockpit', 'cockpit de ordens', 'ordens'],
      NewApplication: ['boleta', 'boleta fácil'],
      OrdersHistory: ['minhas', 'histórico de ordens', 'ordens'],
      AngularFrame_ShoppingCart: ['carrinho', 'carrinho de compras', 'compras'],
      BlockList: ['clientes restritos', 'beta'],
      Trading: ['daytrade', 'beta'],
      AngularFrame_VoluntaryEvents: ['subscrição'],
      AngularFrame_TransactionFlow: ['fluxo', 'fluxo de movimentação', 'movimentação'],
      AngularFrame_DividendHistory: ['proventos'],
      AngularFrame_AvailableProducts: ['produtos disponíveis', 'produtos', 'disponível'],
      Customers: ['meus clientes', 'clientes', 'beta'],
      AngularFrame_Customers: ['meus clientes', 'clientes'],
      AngularFrame_Commissions: ['comissões'],
      AngularFrame_FormsAndTerms: ['fichas e termos', 'ficha', 'termo'],
      AngularFrame_AccessLevels: ['níveis de acesso', 'nível', 'acesso'],
      AngularFrame_ProductControl: ['controle de produtos', 'controle'],
    };
  }, []);

  useEffect(() => {
    if (!keyword || (keyword && keyword.length < 3)) {
      setFoundRoutes([]);
      return;
    }

    setFoundRoutes(
      Object.entries(routesToSearch)
        .filter(([, keywords]) =>
          keywords.some((currentKeyword) =>
            currentKeyword.match(new RegExp(keyword.toLocaleLowerCase(), 'i'))
          )
        )
        .map(([key, keywords]) => ({
          routeName: key,
          routeText: labels[key] || key,
          routeParams: angularFrameRoutesParams[key] ?? {},
          isBeta: keywords.includes('beta'),
          menuIcon: sidebarIcons[key] ?? 'Edit',
        }))
        .splice(0, 5)
    );
  }, [keyword, labels, routesToSearch]);

  return foundRoutes;
};
